import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useChatTabs, ChatTabState, ChatContextAnchor, ChatContextState } from '../contexts/ChatTabsContext';
import { elasticsearchService } from '../services/elasticsearchService';
import { ChatMessageSource, ElasticsearchHit } from '../types/chat';
import { PlayerLink } from '../components/PlayerLink';
import { MessageSquare, LogOut, Terminal, HelpCircle, LogIn, X, Calendar, User, Search, ChevronsUpDown, Plus, Mail, Filter, Locate } from 'lucide-react';
import { clusterManagementService } from '../services/clusterManagementService';
import { motion, AnimatePresence } from 'framer-motion';
import { subDays, format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { LocalizedTimestamp } from '../components/LocalizedTimestamp';
import { parseEsTimestamp } from '../utils/timestamps';
import { getScrollParent } from '../utils/scroll';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

/** Tinted background marking the message a context tab is anchored on. */
const CONTEXT_ANCHOR_BG = 'rgb(var(--accent-t-500-rgb) / 0.14)';

/** Layout-mirroring placeholder rows shown while a tab is loading. */
const LogRowsSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <div aria-hidden="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-2.5 border-b border-glass-200/[0.06]">
        <SkeletonLoader className="h-3 w-24" />
        <SkeletonLoader className="h-3 w-40 max-md:hidden" />
        <SkeletonLoader className="h-4 w-4 rounded" />
        <SkeletonLoader className="h-3 flex-1" />
      </div>
    ))}
  </div>
);

/** Labelled slot in the filter toolbar. */
const FilterField = ({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) => (
  <div className={className}>
    <div className="text-xs font-medium text-glass-400 mb-1.5">{label}</div>
    {children}
  </div>
);

const ChatMessage = ({ message, highlightedUsers, onToggleHighlight, onJumpToContext, isContextAnchor = false, showAbsoluteTime = false }: { 
  message: ChatMessageSource;
  highlightedUsers: { [username: string]: string };
  onToggleHighlight: (username: string) => void;
  /** When provided, shows a "Jump to Context" action and makes the timestamp clickable. */
  onJumpToContext?: () => void;
  /** Marks the message a context tab is centred on. */
  isContextAnchor?: boolean;
  /** Show the absolute local time instead of "x minutes ago" (used in context view). */
  showAbsoluteTime?: boolean;
}) => {
  const { timestamp, user, uuid, to_user, to_uuid, message_text, message_type, server_fqdn } = message;
  const [clusterName, setClusterName] = useState<string | null>(null);
  const [timeAgo, setTimeAgo] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const highlightColor = highlightedUsers[message.user];

  const localTimestamp = useMemo(() => {
    // Ensure the timestamp is correctly interpreted as UTC
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`;
    return new Date(utcTimestamp);
  }, [timestamp]);

  useEffect(() => {
    const updateRelativeTime = () => {
      const seconds = differenceInSeconds(new Date(), localTimestamp);
      if (seconds < 60) {
        setTimeAgo(seconds <= 1 ? `1 second ago` : `${seconds} seconds ago`);
      } else {
        setTimeAgo(formatDistanceToNow(localTimestamp, { addSuffix: true }));
      }
    };

    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [localTimestamp]);

  useEffect(() => {
    let isMounted = true;
    clusterManagementService.getClusterName(server_fqdn).then(resolvedName => {
      if (isMounted && resolvedName !== server_fqdn) setClusterName(resolvedName);
    });
    return () => { isMounted = false; };
  }, [server_fqdn]);

  const renderPrivateMessage = () => (
    <div className="inline italic text-purple-300">
      <PlayerLink uuid={uuid} name={user} showUuid={false} showAvatar={false} /> whispers to <PlayerLink uuid={to_uuid!} name={to_user!} showUuid={false} showAvatar={false} />:
      <span className="text-purple-100 ml-1 break-words">{message_text}</span>
    </div>
  );

  const renderIcon = () => {
    switch (message_type) {
      case 'playerMessage': return <MessageSquare className="w-4 h-4 text-gray-400" />;
      case 'privateMessage': return <Mail className="w-4 h-4 text-purple-400" />;
      case 'playerLeft': return <LogOut className="w-4 h-4 text-red-400" />;
      case 'playerJoin': return <LogIn className="w-4 h-4 text-green-400" />;
      case 'executeCommand': return <Terminal className="w-4 h-4 text-blue-400" />;
      case 'executeCommandFeedback': return <Terminal className="w-4 h-4 text-blue-500/70" />;
      default: return <HelpCircle className="w-4 h-4 text-yellow-400" />;
    }
  };

  const renderMessageContent = () => {
    switch (message_type) {
      case 'playerMessage':
        if (to_user && to_uuid) {
          return renderPrivateMessage();
        }
        return <div className="inline"><PlayerLink uuid={uuid} name={user} showUuid={false} showAvatar={false} /><span className="text-white ml-1 break-words">: {message_text}</span></div>;
      case 'privateMessage':
        return renderPrivateMessage();
      case 'playerLeft':
        return <div className="inline text-glass-400"><PlayerLink uuid={uuid} name={user} showUuid={false} showAvatar={false} /> left the server.</div>;
      case 'playerJoin':
        return <div className="inline text-glass-400"><PlayerLink uuid={uuid} name={user} showUuid={false} showAvatar={false} /> joined the server.</div>;
      case 'executeCommand':
        return <div className="inline text-blue-400"><PlayerLink uuid={uuid} name={user} showUuid={false} showAvatar={false} /> executed: <span className="font-semibold text-blue-300">{message_text}</span></div>;
      case 'executeCommandFeedback':
        return <p className="italic text-blue-500/80">{message_text}</p>;
      default:
        return <div className="flex items-center"><span className="text-yellow-400 mr-2 font-semibold">{message_type}</span><p className="text-gray-300">{message_text || '(No message text)'}</p></div>;
    }
  };

  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const rowBackground = isContextAnchor ? CONTEXT_ANCHOR_BG : (highlightColor || 'transparent');
  const anchorBorder = isContextAnchor ? 'border-l-2 border-l-accent-400' : '';

  // Mobile layout: stacked, tap-to-expand for actions
  if (isMobile) {
    return (
      <div
        className={`font-mono text-[13px] px-3 py-2 border-b border-glass-200/10 touch-manipulation ${anchorBorder}`}
        style={{ backgroundColor: rowBackground }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 mt-0.5">{renderIcon()}</div>
          <div className="flex-1 min-w-0">
            <div className="break-words">{renderMessageContent()}</div>
            <div className="flex items-center gap-2 mt-1 text-[11px] text-glass-500 tabular-nums">
              {showAbsoluteTime ? <LocalizedTimestamp value={timestamp} /> : <span>{timeAgo}</span>}
              <span>·</span>
              <span className="truncate">{clusterName || server_fqdn}</span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="mt-2 flex gap-2 pl-6">
            {onJumpToContext && (
              <button
                onClick={(e) => { e.stopPropagation(); onJumpToContext(); }}
                className="glass-button-sm touch-manipulation"
              >
                <Locate className="w-3.5 h-3.5" />
                Jump to Context
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onToggleHighlight(message.user); }}
              className="glass-button-sm touch-manipulation"
            >
              Highlight User
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop layout: fixed-width columns
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`font-mono text-[13px] px-4 py-1.5 flex items-start gap-4 border-b border-glass-200/[0.06] transition-colors relative ${anchorBorder}`}
      style={{ backgroundColor: rowBackground }}
    >
      {isHovered && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5">
          {onJumpToContext && (
            <button onClick={onJumpToContext} className="glass-button-sm">
              <Locate className="w-3.5 h-3.5" />
              Jump to Context
            </button>
          )}
          <button onClick={() => onToggleHighlight(message.user)} className="glass-button-sm">
            Highlight User
          </button>
        </div>
      )}
      <div className={`flex-shrink-0 ${showAbsoluteTime ? 'w-36 text-xs' : 'w-28'} text-glass-500 pt-0.5 tabular-nums`}>
        {onJumpToContext ? (
          <button
            onClick={onJumpToContext}
            className="text-left hover:text-accent-300 hover:underline"
            title={`${localTimestamp.toLocaleString()} — click to jump to context`}
          >
            {showAbsoluteTime ? <LocalizedTimestamp value={timestamp} className="cursor-pointer" /> : timeAgo}
          </button>
        ) : (
          <span title={localTimestamp.toLocaleString()}>
            {showAbsoluteTime ? <LocalizedTimestamp value={timestamp} /> : timeAgo}
          </span>
        )}
      </div>
      <div className="flex-shrink-0 w-48 flex flex-col pt-0.5" title={server_fqdn}>
        <span className="font-medium text-blue-400/90 truncate">{clusterName || server_fqdn}</span>
        {clusterName && <span className="text-[11px] text-glass-500 -mt-0.5 truncate">{server_fqdn}</span>}
      </div>
      <div className="flex-shrink-0 mt-1">{renderIcon()}</div>
      <div className="flex-grow flex items-baseline pt-0.5">{renderMessageContent()}</div>
    </div>
  );
};

const TokenInput = ({ value, onChange, placeholder, icon: Icon }: any) => {
  const [inputValue, setInputValue] = useState('');
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      if (!value.includes(inputValue.trim())) onChange([...value, inputValue.trim()]);
      setInputValue('');
      e.preventDefault();
    }
    if (e.key === 'Backspace' && !inputValue) onChange(value.slice(0, -1));
  };
  const removeToken = (token: string) => onChange(value.filter((t: string) => t !== token));

  const handleBlur = () => {
    if (inputValue.trim()) {
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  return (
    <div className="glass-input flex items-center flex-wrap gap-1 py-1 px-2">
      <Icon className="w-4 h-4 text-glass-400 flex-shrink-0 mr-0.5" />
      {value.map((token: string) => (
        <span key={token} className="chip chip-removable">
          <span>{token}</span>
          <button type="button" onClick={() => removeToken(token)} title="Remove" aria-label={`Remove ${token}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} onBlur={handleBlur} placeholder={value.length === 0 ? placeholder : ''} className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]" />
    </div>
  );
};

const MultiSelect = ({ options, selected, onChange, placeholder }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const toggleOption = (option: string) => onChange(selected.includes(option) ? selected.filter((s: string) => s !== option) : [...selected, option]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="glass-input w-full flex items-center justify-between gap-2 text-left">
        <span className="truncate">{selected.length > 0 ? `${selected.length} selected` : placeholder}</span>
        <ChevronsUpDown className="w-4 h-4 text-glass-400 flex-shrink-0" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 w-full mt-1 glass-card max-h-60 overflow-y-auto">
            {options.map((option: string) => (
              <div key={option} onClick={() => toggleOption(option)} className="px-3 py-2 text-sm cursor-pointer hover:bg-glass-200/10 flex items-center">
                <div className={`w-4 h-4 border rounded mr-2 flex-shrink-0 ${selected.includes(option) ? 'bg-accent-500 border-accent-300' : 'border-glass-400'}`} />
                <span className="truncate">{option}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TimeRangeSelector = ({ value, onChange }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const presets = [
    { label: 'Last hour', from: subDays(new Date(), 1/24), to: new Date() },
    { label: 'Last 24 hours', from: subDays(new Date(), 1), to: new Date() },
    { label: 'Last 7 days', from: subDays(new Date(), 7), to: new Date() },
    { label: 'Last 30 days', from: subDays(new Date(), 30), to: new Date() },
  ];

  const handleOpen = () => {
    setIsOpen(true);
  };
  
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={handleOpen} className="glass-input w-full flex items-center justify-between gap-2 text-left">
        <span className="truncate text-xs tabular-nums">{format(value.from, 'MMM d, HH:mm')} - {format(value.to, 'MMM d, HH:mm')}</span>
        <Calendar className="w-4 h-4 text-glass-400 flex-shrink-0" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 w-[300px] mt-1 glass-card p-3 space-y-3 right-0">
            <div className="grid grid-cols-2 gap-2">
              {presets.map(p => <button key={p.label} onClick={() => { onChange(p); setIsOpen(false); }} className="glass-button-sm text-center">{p.label}</button>)}
            </div>
            <div><label className="block text-xs font-medium text-glass-400 mb-1.5">From</label><input type="datetime-local" value={format(value.from, "yyyy-MM-dd'T'HH:mm")} onChange={e => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) onChange({ ...value, from: d }); }} className="glass-input w-full" /></div>
            <div><label className="block text-xs font-medium text-glass-400 mb-1.5">To</label><input type="datetime-local" value={format(value.to, "yyyy-MM-dd'T'HH:mm")} onChange={e => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) onChange({ ...value, to: d }); }} className="glass-input w-full" /></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Combobox = ({ options, selected, onChange, placeholder }: { options: string[], selected: string[], onChange: (selected: string[]) => void, placeholder: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const removeOption = (option: string) => {
    onChange(selected.filter(s => s !== option));
  };

  const selectOption = (option: string) => {
    if (!selected.includes(option)) onChange([...selected, option]);
    setInputValue('');
    setIsOpen(false);
  };

  const filteredOptions = options.filter(
    o => o.toLowerCase().includes(inputValue.toLowerCase()) && !selected.includes(o)
  );

  const handleOpen = () => {
    setIsOpen(true);
  };
  
  return (
    <div ref={ref}>
      <div className="glass-input flex items-center flex-wrap gap-1 py-1 px-2" onClick={handleOpen}>
        {selected.map(token => (
          <span key={token} className="chip chip-removable">
            <span>{token}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); removeOption(token); }} title="Remove" aria-label={`Remove ${token}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setIsOpen(true); }}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]"
        />
      </div>
      {isOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 w-full mt-1 glass-card max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div key={option} onClick={() => selectOption(option)} className="px-3 py-2 text-sm cursor-pointer hover:bg-glass-200/10 flex items-center">
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-glass-400">No results found.</div>
            )}
        </motion.div>
      )}
    </div>
  );
};

const FilterBar = ({ tab }: { tab: ChatTabState }) => {
  const { updateFilters } = useChatTabs();
  const { id, filters, aggregations } = tab;
  const [allClusters, setAllClusters] = useState<Record<string, string[]>>({});
  const isMobile = useIsMobile();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    clusterManagementService.getClusterFqdnMapping().then(setAllClusters);
  }, []);

  const handleClusterChange = (selectedClusters: string[]) => {
    const fqdns = selectedClusters.flatMap(c => allClusters[c] || []);
    updateFilters(id, { clusterSearch: selectedClusters, fqdnSearch: fqdns });
  };

  const activeFilterCount = filters.userSearch.length + filters.clusterSearch.length + filters.messageTypeSearch.length;
  
  if (isMobile) {
    return (
      <div className="p-3 space-y-2 border-b border-glass-200/10">
        <div className="flex gap-2">
          <div className="flex-1">
            <TokenInput value={filters.textSearch.split(' ').filter(Boolean)} onChange={(val: string[]) => updateFilters(id, { textSearch: val.join(' ') })} placeholder="Search text..." icon={Search} />
          </div>
          <button
            type="button"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="glass-button flex-shrink-0 touch-manipulation px-3"
            aria-expanded={filtersExpanded}
            aria-label="Toggle filters"
            title="Filters"
          >
            <Filter className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="badge badge-accent px-1.5">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
        {filtersExpanded && (
          <div className="grid grid-cols-1 gap-2">
            <FilterField label="Players">
              <TokenInput value={filters.userSearch} onChange={(val: string[]) => updateFilters(id, { userSearch: val })} placeholder="Add users..." icon={User} />
            </FilterField>
            <FilterField label="Clusters">
              <Combobox
                options={Object.keys(allClusters)}
                selected={filters.clusterSearch}
                onChange={handleClusterChange}
                placeholder="Filter clusters..."
              />
            </FilterField>
            <FilterField label="Message types">
              <MultiSelect options={aggregations.messageTypes} selected={filters.messageTypeSearch} onChange={(val: string[]) => updateFilters(id, { messageTypeSearch: val })} placeholder="Message Types" />
            </FilterField>
            <FilterField label="Time range">
              <TimeRangeSelector value={filters.dateRange} onChange={(val: any) => updateFilters(id, { dateRange: val })} />
            </FilterField>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-3 border-b border-glass-200/10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <FilterField label="Search">
          <TokenInput value={filters.textSearch.split(' ').filter(Boolean)} onChange={(val: string[]) => updateFilters(id, { textSearch: val.join(' ') })} placeholder="Search text..." icon={Search} />
        </FilterField>
        <FilterField label="Players">
          <TokenInput value={filters.userSearch} onChange={(val: string[]) => updateFilters(id, { userSearch: val })} placeholder="Add users..." icon={User} />
        </FilterField>
        <FilterField label="Clusters">
          <Combobox
            options={Object.keys(allClusters)}
            selected={filters.clusterSearch}
            onChange={handleClusterChange}
            placeholder="Filter clusters..."
          />
        </FilterField>
        <FilterField label="Message types">
          <MultiSelect options={aggregations.messageTypes} selected={filters.messageTypeSearch} onChange={(val: string[]) => updateFilters(id, { messageTypeSearch: val })} placeholder="Message Types" />
        </FilterField>
        <FilterField label="Time range">
          <TimeRangeSelector value={filters.dateRange} onChange={(val: any) => updateFilters(id, { dateRange: val })} />
        </FilterField>
      </div>
    </div>
  );
};

const CONTEXT_PAGE_SIZE = 50;

const contextTabName = (esTimestamp: string) => `Context @ ${format(parseEsTimestamp(esTimestamp), 'MMM d HH:mm')}`;

const chatHitToAnchor = (hit: ElasticsearchHit): ChatContextAnchor => ({
  hitId: hit._id,
  timestamp: hit._source.timestamp,
  serverFqdn: hit._source.server_fqdn,
  user: hit._source.user,
});

/**
 * Query for messages around an anchor on the same server. "before" includes
 * the anchor itself (lte, newest-first); "after" is strictly newer (gt,
 * oldest-first), so the two directions never overlap.
 */
const buildChatContextQuery = (anchor: ChatContextAnchor, direction: 'before' | 'after', offset: number) => ({
  size: CONTEXT_PAGE_SIZE,
  from: offset,
  sort: [{ timestamp: { order: direction === 'before' ? 'desc' : 'asc' } }],
  query: {
    bool: {
      must: [
        { term: { 'server_fqdn.keyword': anchor.serverFqdn } },
        { range: { timestamp: direction === 'before' ? { lte: anchor.timestamp } : { gt: anchor.timestamp } } },
      ],
    },
  },
});

const ChatContextTabContent = ({ tab }: { tab: ChatTabState }) => {
  const { updateTabState, updateTabName, toggleUserHighlight } = useChatTabs();
  const { id, highlightedUsers, isLoading } = tab;
  const context = tab.context!;
  const contextRef = useRef(context);
  contextRef.current = context;

  const [clusterName, setClusterName] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;
    setClusterName(null);
    clusterManagementService.getClusterName(context.anchor.serverFqdn).then(resolvedName => {
      if (isMounted && resolvedName !== context.anchor.serverFqdn) setClusterName(resolvedName);
    });
    return () => { isMounted = false; };
  }, [context.anchor.serverFqdn]);

  const loadingRef = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const anchorRowRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledAnchorRef = useRef<string | null>(null);
  const prependAdjustRef = useRef<{ scroller: HTMLElement; prevHeight: number; prevTop: number } | null>(null);

  const loadInitial = useCallback(async () => {
    const anchor = contextRef.current.anchor;
    loadingRef.current = true;
    updateTabState(id, { isLoading: true });
    try {
      const [beforeData, afterData] = await Promise.all([
        elasticsearchService.getChatMessages(buildChatContextQuery(anchor, 'before', 0)),
        elasticsearchService.getChatMessages(buildChatContextQuery(anchor, 'after', 0)),
      ]);
      // Discard the response if the tab was re-anchored while it was in
      // flight; clearing isLoading re-runs the load effect for the new anchor.
      if (contextRef.current.anchor.hitId !== anchor.hitId) {
        updateTabState(id, { isLoading: false });
        return;
      }
      updateTabState(id, {
        isLoading: false,
        context: {
          anchor,
          beforeHits: beforeData.hits.hits,
          afterHits: afterData.hits.hits,
          hasMoreBefore: beforeData.hits.hits.length > 0 && beforeData.hits.total.value > beforeData.hits.hits.length,
          hasMoreAfter: afterData.hits.hits.length > 0 && afterData.hits.total.value > afterData.hits.hits.length,
          initialLoaded: true,
        },
      });
    } catch (err) {
      console.error(err);
      updateTabState(id, { isLoading: false });
    } finally {
      loadingRef.current = false;
    }
  }, [id, updateTabState]);

  const loadMore = useCallback(async (direction: 'before' | 'after') => {
    const current = contextRef.current;
    if (loadingRef.current || !current.initialLoaded) return;
    if (direction === 'before' ? !current.hasMoreBefore : !current.hasMoreAfter) return;
    const anchor = current.anchor;
    loadingRef.current = true;
    updateTabState(id, { isLoading: true });
    try {
      const offset = direction === 'before' ? current.beforeHits.length : current.afterHits.length;
      const data = await elasticsearchService.getChatMessages(buildChatContextQuery(anchor, direction, offset));
      if (contextRef.current.anchor.hitId !== anchor.hitId) {
        updateTabState(id, { isLoading: false });
        return;
      }
      if (direction === 'before') {
        // Content is prepended above the viewport; capture scroll state so we
        // can keep the visible messages in place after the update.
        const scroller = getScrollParent(listRef.current);
        if (scroller) prependAdjustRef.current = { scroller, prevHeight: scroller.scrollHeight, prevTop: scroller.scrollTop };
      }
      const latest = contextRef.current;
      const newContext: ChatContextState = direction === 'before'
        ? {
            ...latest,
            beforeHits: [...latest.beforeHits, ...data.hits.hits],
            hasMoreBefore: data.hits.hits.length > 0 && data.hits.total.value > latest.beforeHits.length + data.hits.hits.length,
          }
        : {
            ...latest,
            afterHits: [...latest.afterHits, ...data.hits.hits],
            hasMoreAfter: data.hits.hits.length > 0 && data.hits.total.value > latest.afterHits.length + data.hits.hits.length,
          };
      updateTabState(id, { isLoading: false, context: newContext });
    } catch (err) {
      console.error(err);
      updateTabState(id, { isLoading: false });
    } finally {
      loadingRef.current = false;
    }
  }, [id, updateTabState]);

  const reanchor = useCallback((hit: ElasticsearchHit) => {
    lastScrolledAnchorRef.current = null;
    updateTabState(id, {
      context: {
        anchor: chatHitToAnchor(hit),
        beforeHits: [],
        afterHits: [],
        hasMoreBefore: false,
        hasMoreAfter: false,
        initialLoaded: false,
      },
    });
    updateTabName(id, contextTabName(hit._source.timestamp));
  }, [id, updateTabState, updateTabName]);

  useEffect(() => {
    if (!context.initialLoaded && !loadingRef.current) loadInitial();
    // isLoading is a dependency so a discarded stale response (which clears
    // it) re-triggers the load for the new anchor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.anchor.hitId, context.initialLoaded, isLoading]);

  // Centre the anchor message once its context has loaded.
  useLayoutEffect(() => {
    if (context.initialLoaded && lastScrolledAnchorRef.current !== context.anchor.hitId) {
      lastScrolledAnchorRef.current = context.anchor.hitId;
      anchorRowRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [context.initialLoaded, context.anchor.hitId]);

  // Keep the viewport stable when older messages are prepended.
  useLayoutEffect(() => {
    const adjust = prependAdjustRef.current;
    if (!adjust) return;
    prependAdjustRef.current = null;
    const delta = adjust.scroller.scrollHeight - adjust.prevHeight;
    if (delta > 0) adjust.scroller.scrollTop = adjust.prevTop + delta;
  }, [context.beforeHits.length]);

  const combined = useMemo(() => {
    const seen = new Set<string>();
    const list: ElasticsearchHit[] = [];
    for (const hit of [...context.beforeHits].reverse()) {
      if (!seen.has(hit._id)) { seen.add(hit._id); list.push(hit); }
    }
    for (const hit of context.afterHits) {
      if (!seen.has(hit._id)) { seen.add(hit._id); list.push(hit); }
    }
    return list;
  }, [context.beforeHits, context.afterHits]);

  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (entry.target === topSentinelRef.current) loadMore('before');
        else if (entry.target === bottomSentinelRef.current) loadMore('after');
      });
    });
    if (topSentinelRef.current) observer.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.initialLoaded, context.beforeHits.length, context.afterHits.length, context.hasMoreBefore, context.hasMoreAfter, isLoading]);

  return (
    <div className="h-full flex flex-col">
      {/* Sticky so it stays visible: the actual scroll container is an ancestor of this component. */}
      <div className="sticky top-0 z-20 px-4 py-2.5 border-b border-accent-500/30 backdrop-blur-md flex items-center gap-x-2 gap-y-1 flex-wrap text-sm" style={{ background: 'var(--glass-bg)' }}>
        <Locate className="w-4 h-4 text-accent-300 flex-shrink-0" />
        <span className="text-glass-300">Showing messages around</span>
        <LocalizedTimestamp value={context.anchor.timestamp} className="font-semibold text-accent-200" />
        <span className="text-glass-300">on</span>
        <span className="font-semibold text-blue-400/90" title={context.anchor.serverFqdn}>{clusterName || context.anchor.serverFqdn}</span>
        <span className="text-xs text-glass-400 sm:ml-auto">All messages on this server are shown — filters do not apply.</span>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto [overflow-anchor:none]">
        {!context.initialLoaded ? (
          <LogRowsSkeleton />
        ) : combined.length === 0 ? (
          <EmptyState bare compact icon={Locate} title="No messages around this time" description="Nothing was logged on this server near the anchor." />
        ) : (
          <>
            {context.hasMoreBefore ? (
              <div ref={topSentinelRef} className="p-3 text-center text-xs text-glass-400">Loading earlier messages...</div>
            ) : (
              <div className="p-3 text-center text-xs text-glass-500">No earlier messages on this server.</div>
            )}
            {combined.map(hit => (
              <div key={hit._id} ref={hit._id === context.anchor.hitId ? anchorRowRef : undefined}>
                <ChatMessage
                  message={hit._source}
                  highlightedUsers={highlightedUsers}
                  onToggleHighlight={(username) => toggleUserHighlight(id, username)}
                  onJumpToContext={() => reanchor(hit)}
                  isContextAnchor={hit._id === context.anchor.hitId}
                  showAbsoluteTime
                />
              </div>
            ))}
            {context.hasMoreAfter ? (
              <div ref={bottomSentinelRef} className="p-3 text-center text-xs text-glass-400">Loading newer messages...</div>
            ) : (
              <div className="p-3 text-center text-xs text-glass-500">No newer messages on this server.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ChatLogTabContent = ({ tab }: { tab: ChatTabState }) => {
  const { updateTabState, updateTabName, toggleUserHighlight, addContextTab } = useChatTabs();
  const { id, filters, hits, totalHits, isLoading, name, highlightedUsers } = tab;
  const prevFiltersRef = useRef(filters);
  const hitsRef = useRef(hits);
  const searchReqIdRef = useRef(0);
  hitsRef.current = hits;

  const generateTabName = useCallback((currentFilters: typeof filters) => {
    const { textSearch, userSearch, clusterSearch, fqdnSearch } = currentFilters;
    if (userSearch.length > 0) return userSearch.join(', ');
    if (textSearch) return `"${textSearch}"`;
    if (clusterSearch.length > 0) return clusterSearch.join(', ');
    if (fqdnSearch.length > 0) return fqdnSearch.join(', ');
    return 'All Messages';
  }, []);

  useEffect(() => {
    // Only auto-rename if the filters have actually changed
    if (JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current)) {
      const prevName = generateTabName(prevFiltersRef.current);
      if (name === 'New Tab' || name === 'All Messages' || name === prevName) {
        updateTabName(id, generateTabName(filters));
      }
      prevFiltersRef.current = filters;
    }
  }, [filters, name, id, updateTabName, generateTabName]);
  
  const handleSearch = useCallback(async (isLoadMore = false) => {
    updateTabState(id, { isLoading: true });
    const reqId = ++searchReqIdRef.current;
    const mustClauses: any[] = [];
    if (filters.textSearch) mustClauses.push({ multi_match: { query: filters.textSearch, fields: ["message_text", "user"] } });
    if (filters.userSearch.length > 0) mustClauses.push({ terms: { 'user.keyword': filters.userSearch } });
    if (filters.fqdnSearch.length > 0) mustClauses.push({ terms: { 'server_fqdn.keyword': filters.fqdnSearch } });
    if (filters.messageTypeSearch.length > 0) mustClauses.push({ terms: { 'message_type.keyword': filters.messageTypeSearch } });
    const range: any = {};
    if (filters.dateRange.from) range.gte = filters.dateRange.from.toISOString();
    if (filters.dateRange.to) range.lte = filters.dateRange.to.toISOString();
    if (Object.keys(range).length > 0) mustClauses.push({ range: { timestamp: range } });
    const query: any = { size: 100, from: isLoadMore ? hitsRef.current.length : 0, sort: [{ timestamp: { order: "desc" } }], query: { bool: { must: mustClauses.length > 0 ? mustClauses : { match_all: {} } } } };
    if (!isLoadMore) {
      query.aggs = { clusters: { terms: { field: 'server_fqdn.keyword', size: 100 } }, messageTypes: { terms: { field: 'message_type.keyword', size: 20 } } };
    }
    try {
      const data = await elasticsearchService.getChatMessages(query);
      if (reqId !== searchReqIdRef.current) return;
      const newHits = isLoadMore ? [...hitsRef.current, ...data.hits.hits] : data.hits.hits;
      const newState: Partial<ChatTabState> = { hits: newHits, totalHits: data.hits.total.value, isLoading: false };
      if (!isLoadMore && (data as any).aggregations) {
        newState.aggregations = { clusters: (data as any).aggregations.clusters.buckets.map((b: any) => b.key), messageTypes: (data as any).aggregations.messageTypes.buckets.map((b: any) => b.key) };
      }
      updateTabState(id, newState);
    } catch (err: any) {
      if (reqId !== searchReqIdRef.current) return;
      console.error(err);
      updateTabState(id, { isLoading: false });
    }
  }, [id, filters, updateTabState]); // Removed isLoading to prevent recreation on state change
  useEffect(() => {
    const handler = setTimeout(() => handleSearch(false), 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]); // handleSearch excluded intentionally to prevent infinite loop

  const loadMoreRef = useRef(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !isLoading && hits.length < totalHits) {
            handleSearch(true);
        }
    });

    const currentRef = loadMoreRef.current;
    if (currentRef) {
        observer.current.observe(currentRef);
    }

    return () => {
        if (currentRef) {
            observer.current?.unobserve(currentRef);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits, isLoading, totalHits]); // handleSearch excluded intentionally to prevent recreation

  const openContextTab = useCallback((hit: ElasticsearchHit) => {
    addContextTab(chatHitToAnchor(hit), contextTabName(hit._source.timestamp));
  }, [addContextTab]);

  return (
    <div className="h-full flex flex-col">
      <FilterBar tab={tab} />
      <div className="flex-1 overflow-y-auto">
        {isLoading && hits.length === 0 ? <LogRowsSkeleton /> : 
         !isLoading && hits.length === 0 ? <EmptyState bare icon={Search} title="No messages found" description="Try widening the time range or removing a filter." /> :
         hits.map(hit => (
           <ChatMessage 
             key={hit._id} 
             message={hit._source} 
             highlightedUsers={highlightedUsers}
             onToggleHighlight={(username) => toggleUserHighlight(id, username)}
             onJumpToContext={() => openContextTab(hit)}
           />
         ))
        }
        {hits.length > 0 && hits.length < totalHits && (
          <div ref={loadMoreRef} className="p-3 text-center text-xs text-glass-400">
            {isLoading ? 'Loading...' : 'Loading more...'}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatLogViewInternal = () => {
  const { tabs, activeTabId, addTab, removeTab, setActiveTab, getActiveTab, updateTabName } = useChatTabs();
  const activeTab = getActiveTab();
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleRename = (tabId: string) => {
    if (editingName.trim()) {
      updateTabName(tabId, editingName.trim());
    }
    setEditingTabId(null);
  };
  
  useEffect(() => { if (tabs.length === 0) addTab(undefined, 'All Messages'); }, [tabs.length, addTab]);
  return (
    <div className="flex flex-col h-full space-y-6">
      <PageHeader
        eyebrow="Audit Logs"
        title="Chat Logs"
        description="Search player chat, whispers, joins, leaves and commands across every server. Double-click a tab to rename it."
        icon={MessageSquare}
      >
        <div className="tabs relative z-10" role="tablist" aria-label="Chat log tabs">
          <AnimatePresence initial={false}>
            {tabs.map(tab => {
              const isActive = activeTabId === tab.id;
              return (
                <motion.div
                  layout
                  key={tab.id}
                  role="tab"
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn('tab cursor-pointer pr-1.5 touch-manipulation', isActive && 'text-white')}
                >
                  {isActive && <motion.div layoutId="activeTabIndicator" className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-accent-400" />}
                  {editingTabId === tab.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleRename(tab.id)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(tab.id)}
                      autoFocus
                      className="bg-transparent outline-none ring-1 ring-accent-400 rounded px-1 text-sm text-white w-36"
                    />
                  ) : (
                    <span onDoubleClick={() => { setEditingTabId(tab.id); setEditingName(tab.name); }} className="relative z-10" title="Double-click to rename">{tab.name}</span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeTab(tab.id); }}
                    className="relative z-10 ml-1 rounded p-1 text-glass-500 hover:text-white hover:bg-glass-200/10 touch-manipulation"
                    title="Close tab"
                    aria-label={`Close ${tab.name}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <button type="button" onClick={() => addTab()} className="btn-icon btn-icon-sm ml-1 shrink-0 touch-manipulation" title="New tab" aria-label="New tab">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </PageHeader>
      <section className="flex-1 glass-card overflow-hidden">
        <div className="h-full overflow-y-auto">
          <AnimatePresence mode="wait">
            {activeTab && (
              <motion.div key={activeTab.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {activeTab.context ? <ChatContextTabContent tab={activeTab} /> : <ChatLogTabContent tab={activeTab} />}
              </motion.div>
            )}
          </AnimatePresence>
          {!activeTab && <EmptyState bare icon={MessageSquare} title="No active tabs" description="Click + to start a new search." />}
        </div>
      </section>
    </div>
  );
};

export function ChatLogView() {
  useDocumentTitle('Chat Logs');
  return <ChatLogViewInternal />;
}
